/**
 * BdcLeadPhonePanel — Phone + SMS interface embedded on the BDC lead
 * workspace. Sits on the right column above the notes card.
 *
 * Features:
 *   • Click-to-call using the Global Softphone (requires the agent to be
 *     online — otherwise falls back to a "Go online first" toast).
 *   • Inline SMS composer with 1500-char limit + character counter
 *   • Latest call + SMS history for this lead
 */
import React, { useEffect, useState } from "react";
import { Phone, PhoneCall, Send, MessageSquare, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { formatPhoneDisplay, formatDuration, toE164 } from "@/lib/phone";
import { useSoftphone, SOFTPHONE_STATUS } from "@/context/SoftphoneContext";
import { relativeTime, StatusPill } from "./bdcUi";

const BdcLeadPhonePanel = ({ leadId, contactPhone, onSmsSent }) => {
  const softphone = useSoftphone();
  const [calls, setCalls] = useState([]);
  const [smsList, setSmsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [smsBody, setSmsBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get("/bdc/calls", { params: { lead_id: leadId, page_size: 25 } }),
        api.get("/bdc/sms", { params: { lead_id: leadId } }),
      ]);
      setCalls(c.data.items || []);
      setSmsList(s.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [leadId]);

  const doCall = () => {
    if (!contactPhone) {
      toast.error("No phone number on file for this lead.");
      return;
    }
    if (softphone.status !== SOFTPHONE_STATUS.READY) {
      toast.error("Softphone must be online to place a call. Toggle 'Go online' first.");
      return;
    }
    softphone.dial(toE164(contactPhone), leadId);
  };

  const doSend = async () => {
    if (!contactPhone) {
      toast.error("No phone number on file to send an SMS.");
      return;
    }
    const body = smsBody.trim();
    if (!body) return;
    setSending(true);
    try {
      await api.post("/bdc/sms/send", { to_phone: toE164(contactPhone), body, lead_id: leadId });
      toast.success("SMS sent");
      setSmsBody("");
      await load();
      onSmsSent?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSending(false); }
  };

  const softphoneReady = softphone.status === SOFTPHONE_STATUS.READY;
  const chars = smsBody.length;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="bdc-lead-phone-panel">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-2">
          <Phone size={12} /> Phone &amp; SMS
        </p>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400" onClick={load} title="Refresh" data-testid="bdc-lead-phone-refresh-btn">
          <RefreshCw size={13} />
        </Button>
      </div>

      {/* Contact + call button */}
      <div className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 mb-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Contact number</p>
          <p className="font-mono text-sm font-semibold text-slate-900 truncate">
            {formatPhoneDisplay(contactPhone) || "—"}
          </p>
        </div>
        <Button
          size="sm"
          disabled={!contactPhone || !softphoneReady}
          className="bg-brand hover:bg-brand/90 text-white"
          onClick={doCall}
          data-testid="bdc-lead-call-btn"
        >
          <PhoneCall size={13} /> {softphoneReady ? "Call" : "Go online"}
        </Button>
      </div>

      {/* SMS composer */}
      <div className="mb-4" data-testid="bdc-lead-sms-composer">
        <Textarea
          value={smsBody}
          onChange={(e) => setSmsBody(e.target.value.slice(0, 1500))}
          rows={3}
          placeholder={contactPhone ? "Type an SMS to this lead…" : "No phone number on file"}
          disabled={!contactPhone || sending}
          className="text-sm"
          data-testid="bdc-lead-sms-input"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-[11px] text-slate-400">{chars} / 1500</span>
          <Button
            size="sm"
            className="bg-gold hover:bg-gold/90 text-slate-900 font-semibold"
            onClick={doSend}
            disabled={!smsBody.trim() || !contactPhone || sending}
            data-testid="bdc-lead-sms-send-btn"
          >
            <Send size={12} /> {sending ? "Sending…" : "Send SMS"}
          </Button>
        </div>
      </div>

      {/* Call + SMS history */}
      <div className="space-y-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Calls</p>
          {loading ? (
            <div className="h-16 bg-slate-100 rounded animate-pulse" />
          ) : calls.length === 0 ? (
            <p className="text-xs text-slate-400">No calls yet.</p>
          ) : (
            <ul className="space-y-1.5 max-h-40 overflow-y-auto">
              {calls.slice(0, 6).map((c) => (
                <li key={c.id} className="text-xs flex items-center gap-2 border-b border-slate-100 pb-1 last:border-0" data-testid={`bdc-lead-phone-call-${c.id}`}>
                  <StatusPill tone={c.direction === "outbound" ? "blue" : "slate"} className="!text-[9px]">
                    {c.direction === "outbound" ? "OUT" : "IN"}
                  </StatusPill>
                  <span className="text-slate-700 flex-1 truncate">{c.status}</span>
                  <span className="text-slate-500">{c.duration_seconds ? formatDuration(c.duration_seconds) : "—"}</span>
                  <span className="text-slate-400">{relativeTime(c.started_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 flex items-center gap-1">
            <MessageSquare size={11} /> SMS
          </p>
          {loading ? (
            <div className="h-16 bg-slate-100 rounded animate-pulse" />
          ) : smsList.length === 0 ? (
            <p className="text-xs text-slate-400">No text messages yet.</p>
          ) : (
            <ul className="space-y-1.5 max-h-48 overflow-y-auto">
              {smsList.slice(-6).map((s) => (
                <li
                  key={s.id}
                  className={`text-xs rounded-lg px-2 py-1.5 ${s.direction === "outbound" ? "bg-brand/10 text-brand" : "bg-slate-100 text-slate-700"}`}
                  data-testid={`bdc-lead-phone-sms-${s.id}`}
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[10px] uppercase tracking-widest font-semibold">
                      {s.direction === "outbound" ? "You" : "Customer"}
                    </span>
                    <span className="text-[10px] text-slate-400">{relativeTime(s.created_at)}</span>
                  </div>
                  <p className="whitespace-pre-line">{s.body}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default BdcLeadPhonePanel;
